import { useEffect, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { Badge } from '@capawesome/capacitor-badge'
import { apiRequest } from '../shared/api/base'

/**
 * Register for push notifications on native platforms.
 * On web, this is a no-op.
 */
export function usePushNotifications(authenticated: boolean) {
  const registered = useRef(false)

  useEffect(() => {
    if (!authenticated) return
    if (!Capacitor.isNativePlatform()) return
    if (registered.current) return
    registered.current = true

    let cleanedUp = false

    async function setup() {
      try {
        const permResult = await FirebaseMessaging.requestPermissions()
        if (permResult.receive !== 'granted') {
          console.warn('[push] Permission not granted:', permResult.receive)
          return
        }

        // getToken() returns the FCM token (handles APNs ↔ FCM mapping)
        const { token } = await FirebaseMessaging.getToken()
        if (cleanedUp || !token) return

        const platform = Capacitor.getPlatform() as 'ios' | 'android'
        await apiRequest('/push/register', {
          method: 'POST',
          body: JSON.stringify({ token, platform }),
        })
      } catch (err) {
        console.error('[push] Failed to register:', err)
      }
    }

    // Listen for token refreshes → re-register with backend
    const tokenListener = FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
      if (cleanedUp) return
      try {
        const platform = Capacitor.getPlatform() as 'ios' | 'android'
        await apiRequest('/push/register', {
          method: 'POST',
          body: JSON.stringify({ token, platform }),
        })
      } catch (err) {
        console.error('[push] Failed to register refreshed token with backend:', err)
      }
    })

    // Foreground: notifications are silenced (presentationOptions = badge only).
    // User already sees live updates via Supabase Realtime.
    const foregroundListener = FirebaseMessaging.addListener('notificationReceived', (_notification) => {
      // No-op — foreground display suppressed in capacitor.config.ts
    })

    // Tap on notification → navigate
    const actionListener = FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
      const data = action.notification?.data ?? {}
      window.dispatchEvent(new CustomEvent('push-navigate', { detail: data }))
    })

    // Clear delivered notifications and badge count on app open and resume
    function clearNotifications() {
      FirebaseMessaging.removeAllDeliveredNotifications().catch(() => {})
      Badge.clear().catch(() => {})
    }

    const resumeListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) clearNotifications()
    })

    setup()
    clearNotifications()

    return () => {
      cleanedUp = true
      tokenListener.then(l => l.remove())
      foregroundListener.then(l => l.remove())
      actionListener.then(l => l.remove())
      resumeListener.then(l => l.remove())
    }
  }, [authenticated])
}
